import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import { PERMISSIONS } from '@siteflow/shared';
import { formatMoney, humanise } from '@/components/admin/licence-status-chip';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { PageHeader } from '@/components/page-header';
import { PermissionGate } from '@/components/permission-gate';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  billingApi,
  billingInvoicesQuery,
  billingKeys,
  billingOverviewQuery,
  billingPlansQuery,
  type BillingPlan,
  type Proration,
} from '@/lib/api/billing';
import { licenceKeys } from '@/lib/api/licensing';
import { messageForError } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';

type Interval = 'month' | 'year';

function PlanCard({
  plan,
  interval,
  current,
  couponCode,
  onChoose,
  pending,
}: {
  plan: BillingPlan;
  interval: Interval;
  current: boolean;
  couponCode: string;
  onChoose: (plan: BillingPlan) => void;
  pending: boolean;
}) {
  const price = plan.prices.find((p) => p.kind === 'base' && p.interval === interval);
  return (
    <Card data-testid={`plan-${plan.key}`} className={current ? 'border-accent' : undefined}>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{plan.name}</CardTitle>
          {current ? <StatusChip tone="info">Current</StatusChip> : null}
        </div>
        <CardDescription>{plan.description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="flex items-baseline gap-1">
          <span className="stat-numeral" data-numeric>
            {price ? formatMoney(price.unitAmount, price.currency) : plan.isCustom ? 'Custom' : 'Free'}
          </span>
          {price ? <span className="text-data text-ink-muted">/ {interval}</span> : null}
        </p>
        <p className="text-data text-ink-muted">
          {plan.productionSites === null ? 'Unlimited' : plan.productionSites} production site
          {plan.productionSites === 1 ? '' : 's'}
          {plan.trialDays ? ` · ${plan.trialDays}-day trial` : ''}
        </p>
        {plan.isCustom ? (
          <Button variant="secondary" size="sm" asChild>
            <a href="mailto:sales@siteflow.example">Contact sales</a>
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={current || !price || pending}
            onClick={() => onChoose(plan)}
            data-coupon={couponCode || undefined}
          >
            {current ? 'Your plan' : `Choose ${plan.name}`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function AddonPanel({ orgId, planPrice }: { orgId: string; planPrice: string | null }) {
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState('1');
  const [proration, setProration] = useState<Proration | null>(null);
  const valid = Number.isInteger(Number(quantity)) && Number(quantity) >= 0 && Number(quantity) <= 100;

  const preview = useMutation({
    mutationFn: () => billingApi.addons(orgId, { quantity: Number(quantity), preview: true }),
    onSuccess: (result) => setProration(result.proration),
    onError: (error) => toast.error(messageForError(error)),
  });
  const apply = useMutation({
    mutationFn: () => billingApi.addons(orgId, { quantity: Number(quantity), preview: false }),
    onSuccess: () => {
      toast.success('Additional site licences updated');
      setProration(null);
      void queryClient.invalidateQueries({ queryKey: billingKeys.all(orgId) });
      void queryClient.invalidateQueries({ queryKey: licenceKeys.all(orgId) });
    },
    onError: (error) => toast.error(messageForError(error)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional production sites</CardTitle>
        <CardDescription>
          Add sites beyond your plan{planPrice ? ` at ${planPrice} each` : ''}. You see the prorated charge before
          anything changes.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="addon-quantity">Extra sites in total</Label>
            <Input
              id="addon-quantity"
              className="w-28"
              inputMode="numeric"
              value={quantity}
              aria-invalid={!valid}
              onChange={(event) => {
                setQuantity(event.target.value);
                setProration(null);
              }}
            />
          </div>
          <Button variant="secondary" disabled={!valid || preview.isPending} onClick={() => preview.mutate()}>
            Preview charge
          </Button>
        </div>
        {proration ? (
          <div className="rounded-md border border-hairline bg-sunken p-4" data-testid="proration">
            <ul className="mb-3 flex flex-col gap-1 text-data">
              {proration.lines.map((line) => (
                <li key={line.description} className="flex justify-between">
                  <span>{line.description}</span>
                  <span data-numeric>{formatMoney(line.amount, proration.currency)}</span>
                </li>
              ))}
            </ul>
            <p className="mb-3 flex justify-between font-medium">
              <span>Due now</span>
              <span data-numeric>{formatMoney(proration.amountDue, proration.currency)}</span>
            </p>
            <Button disabled={apply.isPending} onClick={() => apply.mutate()}>
              {apply.isPending ? 'Applying…' : 'Confirm and add sites'}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function BillingPage() {
  const { orgId } = Route.useParams();
  const { checkout } = Route.useSearch();
  const queryClient = useQueryClient();
  const overview = useQuery(billingOverviewQuery(orgId));
  const plans = useQuery(billingPlansQuery());
  const invoices = useQuery(billingInvoicesQuery(orgId));

  const [interval, setInterval] = useState<Interval>('month');
  const [couponCode, setCouponCode] = useState('');
  const [addonSites, setAddonSites] = useState('0');
  const [confirmCancel, setConfirmCancel] = useState(false);

  const startCheckout = useMutation({
    mutationFn: (plan: BillingPlan) =>
      billingApi.checkout(orgId, {
        planKey: plan.key,
        interval,
        addonQuantity: Number(addonSites) || 0,
        ...(couponCode.trim() ? { couponCode: couponCode.trim() } : {}),
      }),
    onSuccess: (session) => {
      window.location.assign(session.url);
    },
    onError: (error) => toast.error(messageForError(error)),
  });

  const portal = useMutation({
    mutationFn: () => billingApi.portal(orgId),
    onSuccess: (result) => window.location.assign(result.url),
    onError: (error) => toast.error(messageForError(error)),
  });

  const cancel = useMutation({
    mutationFn: () => billingApi.cancel(orgId, { immediately: false }),
    onSuccess: () => {
      toast.success('Subscription will end at the close of this billing period');
      setConfirmCancel(false);
      void queryClient.invalidateQueries({ queryKey: billingKeys.all(orgId) });
    },
    onError: (error) => toast.error(messageForError(error)),
  });

  const subscription = overview.data?.subscription ?? null;
  const addonPrice = plans.data?.items
    .flatMap((plan) => plan.prices)
    .find((price) => price.kind === 'addon_site' && price.interval === interval);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Operations"
        title="Billing"
        description="Plan, subscription state, additional site licences and invoice history for this organisation."
        actions={
          subscription ? (
            <PermissionGate permission={PERMISSIONS.BILLING_MANAGE}>
              <Button variant="secondary" size="sm" disabled={portal.isPending} onClick={() => portal.mutate()}>
                Payment method
              </Button>
            </PermissionGate>
          ) : null
        }
      />

      {checkout === 'success' ? (
        <div role="status" className="rounded-md border border-success/30 bg-success-soft px-4 py-3 text-data text-success">
          Payment received. Your plan and site licences are updated.
        </div>
      ) : null}
      {checkout === 'cancelled' ? (
        <div role="status" className="rounded-md border border-warning/35 bg-warning-soft px-4 py-3 text-data text-warning">
          Checkout cancelled. Nothing was charged.
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
        </CardHeader>
        <CardContent>
          {overview.isPending ? (
            <Skeleton className="h-10 w-72" />
          ) : subscription ? (
            <div className="flex flex-wrap items-center gap-4" data-testid="subscription">
              <StatusChip tone={subscription.status === 'active' ? 'active' : 'pending'}>
                {humanise(subscription.status)}
              </StatusChip>
              <span className="font-medium">{subscription.planKey ?? 'Plan'}</span>
              <span className="text-data text-ink-muted">
                {subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'} {formatDate(subscription.currentPeriodEnd)}
              </span>
              {subscription.trialEnd ? (
                <span className="text-data text-ink-muted">Trial ends {formatDate(subscription.trialEnd)}</span>
              ) : null}
              {!subscription.cancelAtPeriodEnd ? (
                <PermissionGate permission={PERMISSIONS.BILLING_MANAGE}>
                  <Button variant="outlineDanger" size="xs" className="ml-auto" onClick={() => setConfirmCancel(true)}>
                    Cancel subscription
                  </Button>
                </PermissionGate>
              ) : null}
            </div>
          ) : (
            <p className="text-data text-ink-muted">No active subscription. Choose a plan below to begin.</p>
          )}
        </CardContent>
      </Card>

      <PermissionGate permission={PERMISSIONS.BILLING_MANAGE}>
        <section aria-labelledby="plans-heading" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 id="plans-heading" className="section-label">
              Plans
            </h2>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="coupon">Discount code</Label>
                <Input
                  id="coupon"
                  className="w-40"
                  value={couponCode}
                  onChange={(event) => setCouponCode(event.target.value)}
                />
              </div>
              {!subscription ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="checkout-addons">Extra sites</Label>
                  <Input
                    id="checkout-addons"
                    className="w-24"
                    inputMode="numeric"
                    value={addonSites}
                    onChange={(event) => setAddonSites(event.target.value)}
                  />
                </div>
              ) : null}
              <div role="group" aria-label="Billing interval" className="flex rounded-md border border-hairline p-0.5">
                {(['month', 'year'] as const).map((value) => (
                  <Button
                    key={value}
                    size="xs"
                    variant={interval === value ? 'primary' : 'ghost'}
                    aria-pressed={interval === value}
                    onClick={() => setInterval(value)}
                  >
                    {value === 'month' ? 'Monthly' : 'Annual'}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          {plans.isPending ? <Skeleton className="h-48 w-full" /> : null}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {plans.data?.items.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                interval={interval}
                current={subscription?.planKey === plan.key}
                couponCode={couponCode}
                pending={startCheckout.isPending}
                onChoose={(chosen) => startCheckout.mutate(chosen)}
              />
            ))}
          </div>
        </section>

        {subscription ? (
          <AddonPanel
            orgId={orgId}
            planPrice={addonPrice ? formatMoney(addonPrice.unitAmount, addonPrice.currency) : null}
          />
        ) : null}
      </PermissionGate>

      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.data?.items.length ? (
            <ul className="flex flex-col divide-y divide-hairline">
              {invoices.data.items.map((invoice) => (
                <li key={invoice.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5 text-data">
                  <span className="font-mono text-micro">{invoice.number ?? invoice.id.slice(0, 8)}</span>
                  <span className="text-ink-muted">{formatDate(invoice.createdAt)}</span>
                  <span data-numeric>{formatMoney(invoice.total, invoice.currency)}</span>
                  <StatusChip tone={invoice.status === 'paid' ? 'active' : 'pending'}>{invoice.status}</StatusChip>
                  {invoice.hostedInvoiceUrl ? (
                    <a
                      href={invoice.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-accent underline-offset-4 hover:underline"
                    >
                      View
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-data text-ink-muted">No invoices yet.</p>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancel subscription?"
        description="Your sites stay live until the end of the current billing period. After that, publishing is blocked but public content keeps being served."
        confirmLabel="Cancel at period end"
        destructive
        pending={cancel.isPending}
        onConfirm={() => cancel.mutate()}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/billing')({
  validateSearch: z.object({ checkout: z.enum(['success', 'cancelled']).optional().catch(undefined) }),
  component: BillingPage,
});
